import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Autocomplete,
  TextField,
  Divider,
} from "@mui/material";
import { Print, LocalShipping, Person, Store } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useAuth } from "hooks";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useLazyGetActiveVendorProfilesListQuery } from "store/services/vendorProfileApi";
import { useLazyGetShipmentBulkPrintDetailsQuery } from "store/services/shipmentApi";
import { useLazyGetPendingShipmentsByBranchQuery } from "store/services/shipmentApi";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";

const stripDashes = (id) => id.replace(/-/g, "").trim().toUpperCase();

const parseDirectIds = (input) =>
  input
    .split(/[\s,\n]+/)
    .map(stripDashes)
    .filter(Boolean);

const MODES = {
  SHIPMENT: "shipment",
  CUSTOMER: "customer",
  VENDOR: "vendor",
};

const sidebarItems = [
  { key: MODES.SHIPMENT, label: "Shipment", icon: <LocalShipping fontSize="small" /> },
  { key: MODES.CUSTOMER, label: "Customer", icon: <Person fontSize="small" /> },
  { key: MODES.VENDOR, label: "Vendor", icon: <Store fontSize="small" /> },
];

const PrintShipmentLabelModal = ({
  open,
  onClose,
  onGenerateLabels,
  accountId,
  entityId,
}) => {
  const { isVendor, isVendorStaff } = useAuth();
  const { control, reset, watch, setValue } = useForm({
    defaultValues: {
      selectedVendor: null,
      selectedBranch: null,
      selectedCustomer: null,
      selectedShipments: [],
      shipmentIdChips: [],
    },
  });

  const [activeMode, setActiveMode] = useState(MODES.SHIPMENT);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalShipments, setModalShipments] = useState([]);
  const [chipInput, setChipInput] = useState("");

  const [fetchVendorProfiles, { data: vendorProfiles, isLoading: isVendorLoading, isFetching: isVendorFetching, error: vendorError }] =
    useLazyGetActiveVendorProfilesListQuery();
  const [fetchCustomers, { data: customers, isLoading: isCustomerLoading, isFetching: isCustomerFetching, error: customerError }] =
    useLazyGetDDLCustomerQuery();
  const [fetchBranches, { data: branches, isLoading: isBranchLoading, isFetching: isBranchFetching, error: branchError }] =
    useLazyGetDDLBranchListQuery();
  const [fetchPendingShipments, { isLoading: isShipmentsLoading, isFetching: isShipmentsFetching }] =
    useLazyGetPendingShipmentsByBranchQuery();
  const [fetchShipmentBulkPrintDetails, { isLoading: isBulkPrintLoading, isFetching: isBulkPrintFetching }] =
    useLazyGetShipmentBulkPrintDetailsQuery();

  const selectedVendor = watch("selectedVendor");
  const selectedCustomer = watch("selectedCustomer");
  const selectedBranch = watch("selectedBranch");
  const selectedShipments = watch("selectedShipments");
  const shipmentIdChips = watch("shipmentIdChips");

  const resetAll = useCallback(() => {
    setModalShipments([]);
    setActiveMode(MODES.SHIPMENT);
    setErrorMessage("");
    setChipInput("");
    reset({
      selectedVendor: null,
      selectedCustomer: null,
      selectedBranch: null,
      selectedShipments: [],
      shipmentIdChips: [],
    });
  }, [reset]);

  useEffect(() => {
    if (open) {
      fetchVendorProfiles({});
      fetchCustomers({ account: accountId, entity: entityId });
      fetchBranches({ account: accountId, entity: entityId });
      resetAll();
    }
  }, [open, fetchVendorProfiles, fetchCustomers, fetchBranches, accountId, entityId, resetAll]);

  const handleModeSwitch = (mode) => {
    if (mode === activeMode) return;
    setActiveMode(mode);
    setModalShipments([]);
    setErrorMessage("");
    setChipInput("");
    reset({
      selectedVendor: null,
      selectedCustomer: null,
      selectedBranch: null,
      selectedShipments: [],
      shipmentIdChips: [],
    });
  };

  // Chip handlers
  const addChips = useCallback(
    (raw) => {
      const parsed = parseDirectIds(raw);
      if (parsed.length === 0) return;
      const existing = new Set(shipmentIdChips);
      const merged = [...shipmentIdChips, ...parsed.filter((id) => !existing.has(id))];
      setValue("shipmentIdChips", merged);
    },
    [shipmentIdChips, setValue]
  );

  const handleChipInputKeyDown = (e) => {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      addChips(chipInput);
      setChipInput("");
    } else if (e.key === "Backspace" && chipInput === "") {
      const updated = [...shipmentIdChips];
      updated.pop();
      setValue("shipmentIdChips", updated);
    }
  };

  const handleChipInputPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    addChips(pasted);
    setChipInput("");
  };

  const handleDeleteChip = (id) => {
    setValue("shipmentIdChips", shipmentIdChips.filter((c) => c !== id));
  };

  // Fetch shipments for customer/vendor modes
  const fetchShipments = useCallback(() => {
    if (activeMode === MODES.SHIPMENT || !selectedBranch) {
      setModalShipments([]);
      setValue("selectedShipments", []);
      return;
    }

    if (activeMode === MODES.CUSTOMER && !selectedCustomer) {
      setModalShipments([]);
      setValue("selectedShipments", []);
      return;
    }

    if (activeMode === MODES.VENDOR && !selectedVendor && !isVendor && !isVendorStaff) {
      setModalShipments([]);
      setValue("selectedShipments", []);
      return;
    }

    const params = {
      account: accountId,
      entity: entityId,
      sourceBranchId: selectedBranch._id,
    };

    if (activeMode === MODES.VENDOR && selectedVendor?._id) {
      params.vendorId = selectedVendor._id;
    }

    if (activeMode === MODES.CUSTOMER && selectedCustomer?._id) {
      params.customerId = selectedCustomer._id;
    }

    fetchPendingShipments(params)
      .then((response) => {
        if (response.data?.data) {
          const shipments = response.data.data;
          setModalShipments(shipments);
          setValue("selectedShipments", shipments);
        } else {
          setModalShipments([]);
          setValue("selectedShipments", []);
        }
      })
      .catch(() => {
        setErrorMessage("Failed to fetch shipments.");
      });
  }, [
    activeMode,
    selectedVendor,
    selectedCustomer,
    selectedBranch,
    isVendor,
    isVendorStaff,
    fetchPendingShipments,
    accountId,
    entityId,
    setValue,
  ]);

  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handleGenerateLabels = async () => {
    let shipmentIds;

    if (activeMode === MODES.SHIPMENT) {
      shipmentIds = shipmentIdChips;
      if (shipmentIds.length === 0) {
        setErrorMessage("Please enter at least one shipment ID.");
        return;
      }
    } else {
      shipmentIds = selectedShipments.map((s) => stripDashes(s.shipmentId));
      if (shipmentIds.length === 0) {
        setErrorMessage("No pending shipments selected to print.");
        return;
      }
    }

    try {
      const response = await fetchShipmentBulkPrintDetails({
        shipmentIds,
        entityId,
      }).unwrap();

      if (response.data?.length > 0) {
        onGenerateLabels(response.data);
        setErrorMessage("");
        resetAll();
        onClose();
      } else {
        setErrorMessage("No valid shipments found for the selected IDs.");
      }
    } catch (error) {
      setErrorMessage(error?.data?.message || "Failed to fetch shipment details.");
    }
  };

  const isPrintDisabled =
    isBulkPrintLoading ||
    isBulkPrintFetching ||
    (activeMode === MODES.SHIPMENT
      ? shipmentIdChips.length === 0
      : selectedShipments.length === 0);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      "&:hover fieldset": { borderColor: (theme) => theme.palette.primary.main },
      "&.Mui-focused fieldset": { borderColor: (theme) => theme.palette.primary.main },
    },
    "& .MuiInputLabel-root": { color: (theme) => theme.palette.text.secondary },
  };

  const visibleSidebarItems =
    isVendor || isVendorStaff
      ? sidebarItems.filter((i) => i.key === MODES.VENDOR)
      : sidebarItems;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "12px",
          boxShadow: (theme) => theme.shadows[10],
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#424242" : theme.palette.background.paper,
          minHeight: "520px",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg, rgb(167, 212, 248), rgb(146, 195, 241))"
              : "linear-gradient(90deg, #1976d2, #42a5f5)",
          color: (theme) => theme.palette.primary.contrastText,
          fontWeight: 600,
          fontSize: "1.25rem",
          py: 2,
        }}
      >
        Select Shipments to Print
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#424242" : theme.palette.background.paper,
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: "180px",
            flexShrink: 0,
            borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#383838" : "#f5f7fa",
            pt: 2,
          }}
        >
          {visibleSidebarItems.map((item) => (
            <Box
              key={item.key}
              onClick={() => handleModeSwitch(item.key)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                py: 1.5,
                cursor: "pointer",
                borderLeft: "3px solid transparent",
                fontWeight: activeMode === item.key ? 600 : 400,
                fontSize: "0.9rem",
                color: (theme) =>
                  activeMode === item.key
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                borderLeftColor: (theme) =>
                  activeMode === item.key ? theme.palette.primary.main : "transparent",
                backgroundColor: (theme) =>
                  activeMode === item.key
                    ? theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(25, 118, 210, 0.06)"
                    : "transparent",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25, 118, 210, 0.04)",
                  color: (theme) => theme.palette.primary.main,
                },
                userSelect: "none",
              }}
            >
              {item.icon}
              {item.label}
            </Box>
          ))}
        </Box>

        {/* Content Panel */}
        <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: "8px" }}
              onClose={() => setErrorMessage("")}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Shipment — Chip Input */}
          {activeMode === MODES.SHIPMENT && (
            <Box>
              <Box
                onClick={() => document.getElementById("chip-input-field").focus()}
                sx={{
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0,0,0,0.23)"}`,
                  borderRadius: "8px",
                  p: 1.5,
                  minHeight: "180px",
                  cursor: "text",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  alignItems: "flex-start",
                  alignContent: "flex-start",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:focus-within": {
                    borderColor: (theme) => theme.palette.primary.main,
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33`,
                  },
                }}
              >
                {shipmentIdChips.map((id) => (
                  <Box
                    key={id}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 1.25,
                      py: 0.4,
                      borderRadius: "6px",
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(144, 202, 249, 0.16)"
                          : "rgba(25, 118, 210, 0.1)",
                      color: (theme) => theme.palette.primary.main,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      letterSpacing: "0.03em",
                      fontFamily: "monospace",
                      border: (theme) => `1px solid ${theme.palette.primary.main}44`,
                    }}
                  >
                    {id}
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteChip(id);
                      }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ml: 0.25,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        lineHeight: 1,
                        color: (theme) => theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: (theme) => theme.palette.primary.main,
                          color: "#fff",
                        },
                      }}
                    >
                      ×
                    </Box>
                  </Box>
                ))}
                <input
                  id="chip-input-field"
                  value={chipInput}
                  onChange={(e) => setChipInput(e.target.value)}
                  onKeyDown={handleChipInputKeyDown}
                  onPaste={handleChipInputPaste}
                  placeholder={
                    shipmentIdChips.length === 0
                      ? "Type or paste IDs — press Enter or comma to add"
                      : ""
                  }
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "0.875rem",
                    fontFamily: "monospace",
                    color: "inherit",
                    flex: "1 1 180px",
                    minWidth: "180px",
                    padding: "4px 2px",
                  }}
                />
              </Box>
              <Box
                sx={{
                  mt: 0.75,
                  fontSize: "0.75rem",
                  color: (theme) => theme.palette.text.secondary,
                  pl: 0.5,
                }}
              >
                {shipmentIdChips.length} ID(s) added — dashes are stripped automatically
              </Box>
            </Box>
          )}

          {/* Customer Mode */}
          {activeMode === MODES.CUSTOMER && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Controller
                name="selectedCustomer"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={customers || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onChange={(event, value) => {
                      field.onChange(value);
                      setValue("selectedBranch", null);
                      setValue("selectedShipments", []);
                      setModalShipments([]);
                    }}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Customer"
                        placeholder="Select a customer..."
                        variant="outlined"
                        sx={inputSx}
                        helperText={
                          isCustomerLoading || isCustomerFetching
                            ? "Loading customers..."
                            : customerError
                              ? "Error loading customers"
                              : "Select a customer to filter shipments"
                        }
                      />
                    )}
                    disabled={isCustomerLoading || isCustomerFetching}
                  />
                )}
              />
              <Controller
                name="selectedBranch"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={branches || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onChange={(event, value) => {
                      field.onChange(value);
                      setValue("selectedShipments", []);
                    }}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source Branch"
                        placeholder="Select a branch..."
                        variant="outlined"
                        sx={inputSx}
                        helperText={
                          isBranchLoading || isBranchFetching
                            ? "Loading branches..."
                            : branchError
                              ? "Error loading branches"
                              : "Select a branch to filter shipments"
                        }
                      />
                    )}
                    disabled={isBranchLoading || isBranchFetching || !selectedCustomer}
                  />
                )}
              />
              {modalShipments.length > 0 && (
                <Controller
                  name="selectedShipments"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={modalShipments}
                      getOptionLabel={(option) => {
                        if (!option?.shipmentId) return "";
                        return option.status === "Return"
                          ? `${option.shipmentId} (R)`
                          : option.shipmentId;
                      }}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      onChange={(event, value) => field.onChange(value)}
                      value={field.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selected Shipments"
                          placeholder="Select shipments..."
                          variant="outlined"
                          sx={inputSx}
                          helperText={`${field.value.length} pending shipment(s) selected`}
                        />
                      )}
                      disabled={isShipmentsLoading || isShipmentsFetching}
                    />
                  )}
                />
              )}
            </Box>
          )}

          {/* Vendor Mode */}
          {activeMode === MODES.VENDOR && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {!isVendor && !isVendorStaff && (
                <Controller
                  name="selectedVendor"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      options={vendorProfiles || []}
                      getOptionLabel={(option) =>
                        option ? `${option.shopName} - ${option.ownerName}` : ""
                      }
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      onChange={(event, value) => {
                        field.onChange(value);
                        setValue("selectedBranch", null);
                        setValue("selectedShipments", []);
                        setModalShipments([]);
                      }}
                      value={field.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vendor Profile"
                          placeholder="Select a vendor..."
                          variant="outlined"
                          sx={inputSx}
                          helperText={
                            isVendorLoading || isVendorFetching
                              ? "Loading vendors..."
                              : vendorError
                                ? "Error loading vendors"
                                : "Select a vendor to filter shipments"
                          }
                        />
                      )}
                      disabled={isVendorLoading || isVendorFetching}
                    />
                  )}
                />
              )}
              <Controller
                name="selectedBranch"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={branches || []}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    onChange={(event, value) => {
                      field.onChange(value);
                      setValue("selectedShipments", []);
                    }}
                    value={field.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source Branch"
                        placeholder="Select a branch..."
                        variant="outlined"
                        sx={inputSx}
                        helperText={
                          isBranchLoading || isBranchFetching
                            ? "Loading branches..."
                            : branchError
                              ? "Error loading branches"
                              : "Select a branch to filter shipments"
                        }
                      />
                    )}
                    disabled={
                      isBranchLoading ||
                      isBranchFetching ||
                      (!isVendor && !isVendorStaff && !selectedVendor)
                    }
                  />
                )}
              />
              {modalShipments.length > 0 && (
                <Controller
                  name="selectedShipments"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={modalShipments}
                      getOptionLabel={(option) => {
                        if (!option?.shipmentId) return "";
                        return option.status === "Return"
                          ? `${option.shipmentId} (R)`
                          : option.shipmentId;
                      }}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      onChange={(event, value) => field.onChange(value)}
                      value={field.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Selected Shipments"
                          placeholder="Select shipments..."
                          variant="outlined"
                          sx={inputSx}
                          helperText={`${field.value.length} pending shipment(s) selected`}
                        />
                      )}
                      disabled={isShipmentsLoading || isShipmentsFetching}
                    />
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "#424242" : theme.palette.background.paper,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            color: (theme) => theme.palette.text.primary,
            borderColor: (theme) => theme.palette.divider,
            "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateLabels}
          startIcon={<Print />}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: (theme) => theme.shadows[2],
            "&:hover": { boxShadow: (theme) => theme.shadows[4] },
          }}
          disabled={isPrintDisabled}
        >
          Print All
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrintShipmentLabelModal;